import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';
import tarefasService from 'src/services/tarefasService';
import { getListaUsuariosByClienteId } from 'src/redux/slices/user';
import { useSnackbar } from 'notistack';
import TarefaCard, { columnsFromWorkflow } from 'src/pages/dashboard/Tarefas/TarefaCard';

const FALLBACK_STATUS = {
  'A Fazer': 'bg-amber-50 text-amber-700 border-amber-200',
  'Em Progresso': 'bg-blue-50 text-blue-700 border-blue-200',
  'Concluído': 'bg-green-50 text-green-700 border-green-200',
};

function StatusBadge({ tarefa }) {
  const status = tarefa.status;
  const workflow = tarefa.workflow_id;
  if (workflow?.estagios) {
    const estagio = workflow.estagios.find(e => e.nome === status);
    if (estagio?.cor) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] font-medium border"
          style={{ backgroundColor: `${estagio.cor}15`, color: estagio.cor, borderColor: `${estagio.cor}40` }}
        >
          {status}
        </Badge>
      );
    }
  }
  const className = FALLBACK_STATUS[status] || FALLBACK_STATUS['A Fazer'];
  return (
    <Badge variant="outline" className={`text-[10px] font-medium ${className}`}>
      {status}
    </Badge>
  );
}

export default function DrawerNegocioTarefas({ lead, user, refreshTrigger, onOpenNovaTarefa, onClickTarefa }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { usuariosList } = useSelector((state) => state.user);
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [vinculando, setVinculando] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (user?.clienteId && usuariosList.length === 0) {
      dispatch(getListaUsuariosByClienteId(user.clienteId));
    }
  }, [user?.clienteId, usuariosList.length, dispatch]);

  const loadTarefas = useCallback(async () => {
    if (!lead?._id || !user?.clienteId) return;
    setLoading(true);
    try {
      const res = await tarefasService.listar(user.clienteId, { lead_id: lead._id });
      if (!res.error) setTarefas(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar tarefas do lead:', error);
    } finally {
      setLoading(false);
    }
  }, [lead?._id, user?.clienteId]);

  useEffect(() => {
    setTarefas([]);
    setShowSearch(false);
    setSearchTerm('');
    setSearchResults([]);
    loadTarefas();
  }, [lead?._id, refreshTrigger, loadTarefas]);

  const searchTarefas = useCallback((termo) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!termo || termo.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await tarefasService.listar(user?.clienteId, { search: termo, limit: 10 });
        const results = res.data || [];
        const tarefaIds = tarefas.map(t => t._id);
        setSearchResults(results.filter(r => !tarefaIds.includes(r._id)));
      } catch (error) {
        console.error('Erro ao pesquisar tarefas:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
  }, [user?.clienteId, tarefas]);

  const handleVincularTarefa = async (tarefaId) => {
    setVinculando(tarefaId);
    try {
      await tarefasService.editar(tarefaId, { lead_id: lead._id });
      setSearchResults(prev => prev.filter(r => r._id !== tarefaId));
      setSearchTerm('');
      setShowSearch(false);
      loadTarefas();
    } catch (error) {
      console.error('Erro ao vincular tarefa:', error);
    } finally {
      setVinculando(null);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    let previousStatus;
    setTarefas(prev => prev.map(t => {
      if (t._id !== taskId) return t;
      previousStatus = t.status;
      return { ...t, status: newStatus };
    }));
    try {
      const response = await tarefasService.atualizarStatus(taskId, {
        status: newStatus,
        userId: user?._id || user?.id,
      });
      if (response.error) {
        setTarefas(prev => prev.map(t => t._id === taskId ? { ...t, status: previousStatus } : t));
        enqueueSnackbar(response.message || 'Não foi possível atualizar o status', { variant: 'error' });
      }
    } catch (error) {
      setTarefas(prev => prev.map(t => t._id === taskId ? { ...t, status: previousStatus } : t));
      enqueueSnackbar('Não foi possível atualizar o status', { variant: 'error' });
    }
  };

  const handleTaskUpdate = async (taskId, patch) => {
    const previous = tarefas.find(t => t._id === taskId);
    if (!previous) return;

    setTarefas(prev => prev.map(t => {
      if (t._id !== taskId) return t;
      const next = { ...t, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'assigned_to')) {
        if (!patch.assigned_to) {
          next.assigned_to = null;
        } else {
          const u = usuariosList.find(userItem => userItem._id === patch.assigned_to);
          next.assigned_to = u
            ? { _id: u._id, nome: u.nome, nomeFantasia: u.nomeFantasia, email: u.email }
            : patch.assigned_to;
        }
      }
      return next;
    }));

    try {
      const response = await tarefasService.editar(taskId, patch);
      if (response.error) {
        setTarefas(prev => prev.map(t => t._id === taskId ? previous : t));
        enqueueSnackbar(response.message || 'Não foi possível atualizar a tarefa', { variant: 'error' });
        return;
      }
      if (response.data) {
        setTarefas(prev => prev.map(t => t._id === taskId ? response.data : t));
      }
    } catch (error) {
      setTarefas(prev => prev.map(t => t._id === taskId ? previous : t));
      enqueueSnackbar('Não foi possível atualizar a tarefa', { variant: 'error' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#8b8d90]">Tarefas</h3>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] rounded-lg px-2.5 border-gray-200 dark:border-[#383838]"
            onClick={() => { setShowSearch(prev => !prev); setSearchTerm(''); setSearchResults([]); }}
          >
            <Iconify icon="eva:link-2-outline" width={13} className="mr-1" />
            Vincular
          </Button>
          <Button
            size="sm"
            className="h-7 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11px] rounded-lg px-2.5"
            onClick={onOpenNovaTarefa}
          >
            <Iconify icon="eva:plus-fill" width={13} className="mr-1" />
            Criar
          </Button>
        </div>
      </div>

      {showSearch && (
        <div className="space-y-2 p-2.5 mb-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="relative">
            <Iconify icon="eva:search-outline" width={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar tarefa..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); searchTarefas(e.target.value); }}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20"
              autoFocus
            />
          </div>
          {searchLoading && (
            <p className="text-[10px] text-gray-400 text-center py-2">Buscando...</p>
          )}
          {!searchLoading && searchTerm.length >= 2 && searchResults.length === 0 && (
            <p className="text-[10px] text-gray-400 text-center py-2">Nenhum resultado</p>
          )}
          {searchResults.length > 0 && (
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {searchResults.map((item) => (
                <div
                  key={item._id}
                  onClick={() => !vinculando && handleVincularTarefa(item._id)}
                  className="p-2 rounded-md hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors flex items-center justify-between gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge tarefa={item} />
                    </div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 truncate mt-0.5">
                      {item.title || 'Sem título'}
                    </p>
                  </div>
                  {vinculando === item._id ? (
                    <Iconify icon="svg-spinners:ring-resize" width={14} className="text-[#7C3AED] flex-shrink-0" />
                  ) : (
                    <Iconify icon="eva:link-2-outline" width={14} className="text-gray-400 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && tarefas.length === 0 && (
        <p className="text-xs text-gray-400">Carregando...</p>
      )}

      {!loading && tarefas.length === 0 && (
        <p className="text-xs text-gray-400 italic">Nenhuma tarefa vinculada a esta oportunidade ainda.</p>
      )}

      {tarefas.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {tarefas.map((tarefa) => {
            const columns = columnsFromWorkflow(tarefa.workflow_id);
            return (
              <TarefaCard
                key={tarefa._id}
                task={tarefa}
                columns={columns}
                lastColumnId={columns[columns.length - 1]?.id}
                usuariosList={usuariosList}
                showLead={false}
                onEdit={onClickTarefa}
                onStatusChange={handleStatusChange}
                onUpdate={handleTaskUpdate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
